import {
  Vault__factory,
  WeightedPool__factory
} from '@balancer-labs/typechain';
import { Contract } from '@ethersproject/contracts';
import { TransactionResponse, Web3Provider } from '@ethersproject/providers';
import { configService } from '@/services/config/config.service';
import BigNumber from 'bignumber.js';
import { BigNumber as EPBigNumber } from '@ethersproject/bignumber';
import { sendTransaction, callStatic } from '@/lib/utils/balancer/web3';
import { defaultAbiCoder } from '@ethersproject/abi';
import { AddressZero } from '@ethersproject/constants';
import { PoolSeedToken } from '@/composables/pools/usePoolCreation';
import { toNormalizedWeights } from '@balancer-labs/balancer-js';
import TOPICS from '@/constants/topics';
import { default as MiniChefV2 } from '@/lib/abi/MiniChefV2.json';
import { default as VeFlake } from '@/lib/abi/VeFlake.json';

type Address = string;

export interface CreatePoolReturn {
  id: string;
  address: Address;
}

const JOIN_KIND_INIT = 0;

export interface JoinPoolRequest {
  assets: Address[];
  maxAmountsIn: string[];
  userData: any;
  fromInternalBalance: boolean;
}

export default class NormalFarmService {
  public async deposit(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return sendTransaction(provider, farmPool, MiniChefV2, 'deposit', params);
  }

  public async withdraw(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return sendTransaction(
      provider,
      farmPool,
      MiniChefV2,
      'withdrawAndHarvest',
      params
    );
  }

  public async harvest(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return sendTransaction(provider, farmPool, MiniChefV2, 'harvest', params);
  }

  public async getPendingFlake(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return callStatic(provider, farmPool, MiniChefV2, 'pendingFlake', params);
  }

  public async boostDeposit(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return sendTransaction(
      provider,
      farmPool,
      MiniChefV2,
      'boostDeposit',
      params
    );
  }

  public async boostWithdraw(provider: Web3Provider, params: Array<string>) {
    const farmPool = configService.network.addresses.farmPool as string;
    return sendTransaction(
      provider,
      farmPool,
      MiniChefV2,
      'boostWithdraw',
      params
    );
  }

  public async stakeFlake(provider: Web3Provider, params: Array<string>) {
    const flakePool = configService.network.addresses.flakePool as string;
    return sendTransaction(provider, flakePool, VeFlake, 'enter', params);
  }

  public async leaveApply(provider: Web3Provider, params: Array<string>) {
    const flakePool = configService.network.addresses.flakePool as string;
    return sendTransaction(provider, flakePool, VeFlake, 'leaveApply', params);
  }

  public async leave(provider: Web3Provider) {
    const flakePool = configService.network.addresses.flakePool as string;
    return sendTransaction(provider, flakePool, VeFlake, 'leave', []);
  }

  public async cancelLeave(provider: Web3Provider) {
    const flakePool = configService.network.addresses.flakePool as string;
    return sendTransaction(provider, flakePool, VeFlake, 'cancelLeave', []);
  }

  public async details(
    provider: Web3Provider,
    createPoolTransaction: TransactionResponse
  ): Promise<CreatePoolReturn> {
    const receipt: any = await createPoolTransaction.wait();
    let poolAddress;
    if (receipt.events) {
      const events = receipt.events.filter(e => e.event === 'PoolCreated');
      if (events.length > 0 && events[0].args.length > 0) {
        poolAddress = events[0].args[0];
      }
    }

    if (!poolAddress) {
      const logs = receipt.logs.filter(
        l => l.topics?.length > 0 && l.topics[0] === TOPICS.PoolCreated
      );
      poolAddress = logs[0].address;
    }

    const pool = new Contract(poolAddress, WeightedPool__factory.abi, provider);
    const poolId = await pool.getPoolId();

    const poolDetails: CreatePoolReturn = {
      id: poolId,
      address: poolAddress
    };

    return poolDetails;
  }

  public async initJoin(
    provider: Web3Provider,
    poolId: string,
    sender: Address,
    receiver: Address,
    tokenAddresses: Address[],
    initialBalancesString: string[]
  ): Promise<TransactionResponse> {
    const initUserData = defaultAbiCoder.encode(
      ['uint256', 'uint256[]'],
      [JOIN_KIND_INIT, initialBalancesString]
    );

    const value = this.value(initialBalancesString, tokenAddresses);

    tokenAddresses = this.parseTokensIn(tokenAddresses);

    const joinPoolRequest: JoinPoolRequest = {
      assets: tokenAddresses,
      maxAmountsIn: initialBalancesString,
      userData: initUserData,
      fromInternalBalance: false
    };

    const vaultAddress = configService.network.addresses.vault;
    return sendTransaction(
      provider,
      vaultAddress,
      Vault__factory.abi,
      'joinPool',
      [poolId, sender, receiver, joinPoolRequest],
      { value }
    );
  }

  public calculateTokenWeights(tokens: PoolSeedToken[]): string[] {
    const weights: EPBigNumber[] = tokens.map((token: PoolSeedToken) => {
      const normalizedWeight = new BigNumber(token.weight).multipliedBy(
        new BigNumber(1e16)
      );
      return EPBigNumber.from(normalizedWeight.toString());
    });
    const normalizedWeights = toNormalizedWeights(weights);
    const weightStrings = normalizedWeights.map((weight: EPBigNumber) => {
      return weight.toString();
    });

    return weightStrings;
  }

  private value(amountsIn: string[], tokensIn: string[]): EPBigNumber {
    let value = '0';
    const nativeAsset = configService.network.nativeAsset;

    amountsIn.forEach((amount, i) => {
      if (tokensIn[i] === nativeAsset.address) {
        value = amount;
      }
    });

    return EPBigNumber.from(value);
  }

  private parseTokensIn(tokensIn: string[]): string[] {
    const nativeAsset = configService.network.nativeAsset;

    return tokensIn.map(address =>
      address === nativeAsset.address ? AddressZero : address
    );
  }
}
